import styled from "@xstyled/styled-components";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { useMutation } from "@apollo/client";

import { QuestionComponentProps } from "..";

import { Button, Spacing, Text } from "@otta/design";
import { Loading } from "@otta/search/components/Loading";
import { palette, pxToRem } from "@otta/design-tokens";
import { Icon } from "@otta/icons";
import { Link } from "@otta/search/components/Link";
import {
  ParseCvDocument,
  UserCvParsingStatusDocument,
} from "@otta/search/schema";
import { useQuery } from "@otta/search/apollo";
import { useExperiment } from "@otta/search/hooks/unleash";
import { Experiment } from "@otta/search/constants/experiments";

const ActionIcon = styled(Icon).attrs({ icon: "checkmark" })`
  background-color: ${palette.brand.green};
  border-radius: 100%;
  justify-content: center;
  margin-right: sm;
  height: ${pxToRem(24)};
  width: ${pxToRem(24)};
  min-height: ${pxToRem(24)};
  min-width: ${pxToRem(24)};
`;

const ActionText = styled.div`
  text-align: left;
`;

const ActionTextContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
`;

const Card = styled.div`
  background-color: ${palette.brand.white};
  border-radius: lg;
  padding: lg;
`;

const ClickableCard = styled.div`
  background-color: ${palette.brand.white};
  border-radius: lg;
  padding: lg;
  cursor: pointer;
`;

const CVUploadButton = styled(Button).attrs({ level: "primary" })`
  margin: lg;
`;

const CVUploadCard = styled.div`
  background-color: ${palette.grayscale.shade50};
  border: 2px dashed ${palette.grayscale.shade100};
  border-radius: md;
  cursor: pointer;
  padding: lg;

  &:hover {
    background-color: ${palette.grayscale.shade100};
  }
`;

const CVUploadHeader = styled(Text).attrs({ size: 1 })`
  font-weight: 600;
  padding: sm;
`;

const Divider = styled.hr`
  border-width: ${pxToRem(1)} 0 0 0;
  border-top: ${pxToRem(1)} solid ${palette.grayscale.shade200};
`;

const ErrorText = styled(Text)`
  color: ${palette.brand.red};
  font-style: bold;
  font-size: sm;
`;

const NoCVCardContent = styled.div`
  text-align: left;
  padding-top: lg;
  padding-left: lg;
  display: grid;
  grid-template-columns: 60% 40%;
`;

const NoCVCardHeader = styled.div`
  display: flex;
  flex-direction: row;
  text-align: left;
  justify-content: space-between;
`;

const NoCVCardList = styled.ul`
  padding: 0 0 0 ${pxToRem(18)};
`;

const NoCvCardImage = styled.img`
  width: 100%;
`;

const UnderlinedLink = styled(Link)`
  font-weight: 400;
  text-decoration: underline;
  &:hover {
    font-weight: 600;
  }
`;

export function CVUpload({
  setNextEnabled,
  setShowNegative,
}: QuestionComponentProps): React.ReactElement {
  const { variant: CvSignupVariant } = useExperiment(Experiment.CVSignup);
  const CvSignupEnabled = CvSignupVariant === "variant";
  const location = useLocation();
  const navigate = useNavigate();
  const [noCVCardOpen, setNoCVCardOpen] = useState(false);
  const [cvProcessing, setCvProcessing] = useState(false);
  const [cvParsingStatus, setCvParsingStatus] = useState("NOT_STARTED");
  const [fileError, setFileError] = useState<string | null>(null);

  // ensure user can't access the page directly if the signup isn't enabled
  if (!CvSignupEnabled) {
    navigate("/initial-preferences/");
  }

  // we don't allow going back on the signup screen once is here
  useEffect(() => {
    setShowNegative?.(false);
  }, [setShowNegative]);

  const { startPolling, stopPolling } = useQuery(UserCvParsingStatusDocument, {
    onCompleted(data) {
      if (data.currentUser) {
        setCvParsingStatus(data.currentUser.cvParsingStatus);
      }
    },
    onError() {
      setCvParsingStatus("FAILED");
    },
  });

  const [parse, { loading: cvUploadLoading }] = useMutation(ParseCvDocument, {
    onCompleted() {
      setCvProcessing(true);
      startPolling(1000);
    },
    onError() {
      setCvParsingStatus("FAILED");
    },
  });

  useEffect(() => {
    if (cvParsingStatus !== "PROCESSING") {
      setCvProcessing(false);
      stopPolling();
    }

    if (cvParsingStatus === "COMPLETED") {
      setNextEnabled?.(true);
      navigate("/initial-preferences/preferences-review");
    } else if (cvParsingStatus === "FAILED") {
      navigate("/initial-preferences/quality", {
        replace: true,
        state: {
          toast: {
            message:
              "Something went wrong when processing your CV, please fill up the quiz manually",
          },
        },
      });
    }
  }, [cvParsingStatus, navigate, setNextEnabled, stopPolling]);

  const handleFileUpload = (file: File) => {
    setFileError(null);
    parse({ variables: { file } });
  };

  // when I drag a file onto the page and drop outisde of the zone, nothing
  // heppens which is expected. But I still see the green + sign even though
  // nothing will happen. I expect to see a difference in behaviour between
  // inside and outside of the dropzone.
  // Looks like fixing this is a bit of a pain so leaving as is for now
  // to be checked with Janina

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0) {
        handleFileUpload(acceptedFiles[0]);
      }
    },
    onDropRejected: rejectedFiles => {
      if (rejectedFiles.length > 0) {
        switch (rejectedFiles[0].errors[0].code) {
          case "file-too-large":
            setFileError("File size must be less than 5MB");
            break;
          case "file-invalid-type":
            setFileError("File must be a PDF");
            break;
          default:
            setFileError("Invalid file. Please try again");
            break;
        }
      }
    },
    accept: { "application/pdf": [".pdf"] },
    maxSize: 5 * 1024 * 1024,
    maxFiles: 1,
    disabled: cvUploadLoading,
  });

  return (
    <Spacing size={3}>
      <Card>
        <CVUploadCard {...getRootProps()}>
          <input {...getInputProps()} data-testid="cv-input" />
          <CVUploadHeader>Upload your CV</CVUploadHeader>
          <Text>
            Drag & drop a PDF file below 5MB
            <br />
            here or
          </Text>
          {cvUploadLoading || cvProcessing ? (
            <Loading />
          ) : (
            <CVUploadButton>Browse files</CVUploadButton>
          )}

          {fileError && <ErrorText>{fileError}</ErrorText>}
        </CVUploadCard>
      </Card>
      <ClickableCard
        onClick={() => setNoCVCardOpen(!noCVCardOpen)}
        data-testid="toggle-no-cv-accordion"
      >
        <NoCVCardHeader>
          <Text>No CV? Import your LinkedIn profile instead</Text>
          <Icon icon={noCVCardOpen ? "chevron-up" : "chevron-down"} />
        </NoCVCardHeader>
        {noCVCardOpen && (
          <NoCVCardContent>
            <NoCVCardList>
              <Spacing size={-6}>
                <li>
                  Log in to{" "}
                  <a
                    href="https://www.linkedin.com/"
                    alt-text="LinkedIn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn
                  </a>
                </li>
                <li>Click on 'Me' at the top (your profile photo)</li>
                <li>Select 'View profile' from the dropdown</li>
                <li>On your profile page, click on the 'Resource' button</li>
                <li>Select 'Save to PDF' from the dropdown menu</li>
              </Spacing>
            </NoCVCardList>
            <NoCvCardImage
              src="https://storage.googleapis.com/otta-static/images/search-app/linkedin_cv_export_instructions_screenshot.png"
              alt="LinkedIn CV export instructions"
            />
          </NoCVCardContent>
        )}
      </ClickableCard>
      <UnderlinedLink
        to={{
          pathname: "/initial-preferences/quality",
          search: location.search,
        }}
        state={{
          noCvRedirect: true,
        }}
        underline={true}
      >
        I don't have a CV
      </UnderlinedLink>
      <Divider />
      <Text>We will use this to: </Text>
      <ActionTextContainer>
        <ActionIcon />
        <ActionText>
          Set your job preferences with AI to help us{" "}
          <strong>match you with the right roles</strong>
        </ActionText>
      </ActionTextContainer>
      <ActionTextContainer>
        <ActionIcon />
        <ActionText>
          <strong>Keep your job search private</strong> from your current
          employer
        </ActionText>
      </ActionTextContainer>
      <ActionTextContainer>
        <ActionIcon />
        <ActionText>
          Make your profile visible to recruiters so{" "}
          <strong>companies can contact you</strong> about relevant roles
        </ActionText>
      </ActionTextContainer>
    </Spacing>
  );
}
